/* eslint-disable @typescript-eslint/no-throw-literal */
import { MifeParcel } from '@/components/MifeParcel';
import { signOut, useAuth } from '@/features/auth/store/authStore';
import { isProfileComplete } from '@/features/auth/utils/isProfileComplete';
import { eventBus } from '@/libs/eventBus';
import background from '@repo/assets/shared/images/login-background';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

/**
 * TT-137 — first-login profile completion for admin-created (Wakala) investors.
 * The page itself lives in the Vue app (version-one) and is rendered here via
 * <MifeParcel/>, the same way /login is. On submit Vue saves the attributes and
 * emits `mife:profile-completed` with the values; we record them (authStore
 * persists + patches the user so the gate clears immediately and across reloads)
 * and route the user onward — no token refresh, no re-login.
 *
 * Still requires a valid session (the gate in _authenticated.tsx redirects
 * incomplete accounts here; this guard just bounces a logged-out user to login).
 */
export const Route = createFileRoute('/complete-profile')({
  beforeLoad: () => {
    const { isAuthenticated, user } = useAuth.getState();
    if (!isAuthenticated()) {
      signOut();
      throw redirect({ to: '/login', search: { to: '/complete-profile' } });
    }
    // An already-complete user has no business here; a direct navigation would
    // let them re-submit and overwrite local state. Bounce them to the dashboard.
    if (isProfileComplete(user)) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: CompleteProfile,
});

function CompleteProfile() {
  const completeProfile = useAuth.use.completeProfile();
  const navigate = useNavigate();

  useEffect(() => {
    const onProfileCompleted = (event: { birthdate: string; phone_number: string; redirectTo?: string }) => {
      completeProfile({ birthdate: event.birthdate, phone_number: event.phone_number });
      navigate({ to: event.redirectTo ?? '/dashboard' });
    };

    eventBus.on('mife:profile-completed', onProfileCompleted);
    return () => eventBus.off('mife:profile-completed', onProfileCompleted);
  }, [navigate, completeProfile]);

  return (
    <div
      style={{ backgroundImage: `url(${background})` }}
      className="bg-img min-h-screen bg-cover flex flex-col"
    >
      <MifeParcel />
    </div>
  );
}
